import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { compositeLayer } from "../pixel/blend";
import type { Command } from "./Command";

// Composite every visible raster layer (in tree order, bottom-first) into a
// single new raster at document size. Hidden layers are preserved, untouched,
// at their original tree positions. The new merged layer is appended to the
// top of the root layer list.
//
// Caveats:
// - Visible groups are flattened by recursing through their children in
//   bottom-first order. Group-level opacity/blend mode are NOT modeled here;
//   children composite as if the group were "pass through" at opacity 1.
// - Non-raster layers (groups themselves) contribute only via their visible
//   raster descendants.
// - Invert restores the prior layer list verbatim.

const MERGED_ID = "merged-visible";

export class MergeVisibleCommand implements Command {
  readonly kind = "merge-visible";
  private previous: readonly Layer[] | null = null;

  apply(doc: Document): Document {
    this.previous = doc.layers;
    const visible = collectVisibleRasters(doc.layers);
    if (visible.length === 0) return doc;

    const merged = new PixelBuffer(doc.width, doc.height);
    for (const r of visible) {
      const projected = projectOntoCanvas(merged.width, merged.height, r);
      compositeLayer(merged.data, projected, r.blendMode, r.opacity);
    }
    merged.markDirty();

    const newLayer: RasterLayer = {
      kind: "raster",
      id: uniqueId(doc.layers, MERGED_ID),
      name: "Merged",
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels: merged,
    };

    // Drop visible layers from the tree; keep hidden ones in place.
    const remaining = filterTree(doc.layers, (l) => !isLayerVisible(l, doc.layers));
    return { ...doc, layers: [...remaining, newLayer] };
  }

  invert(before: Document, _after: Document): Command {
    const prev = this.previous ?? before.layers;
    return new RestoreLayersCommand(prev);
  }
}

// Internal inverse: rewinds the layer list to the captured snapshot.
class RestoreLayersCommand implements Command {
  readonly kind = "merge-visible/restore";
  constructor(private readonly layers: readonly Layer[]) {}

  apply(doc: Document): Document {
    return { ...doc, layers: this.layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new MergeVisibleCommand();
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function collectVisibleRasters(layers: readonly Layer[]): RasterLayer[] {
  const out: RasterLayer[] = [];
  walk(layers, true, out);
  return out;
}

function walk(layers: readonly Layer[], parentVisible: boolean, acc: RasterLayer[]): void {
  for (const l of layers) {
    const v = parentVisible && l.visible;
    if (!v) continue;
    if (l.kind === "raster") acc.push(l);
    else if (l.kind === "group") walk(l.children, v, acc);
  }
}

// Returns whether the given layer (anywhere in the tree) is visible end-to-end
// — i.e. itself visible and every ancestor visible. Used to decide which
// layers to delete when "flattening" visible layers.
function isLayerVisible(layer: Layer, roots: readonly Layer[]): boolean {
  const chain: Layer[] = [];
  if (!findChain(roots, layer.id, chain)) return false;
  return chain.every((l) => l.visible);
}

function findChain(layers: readonly Layer[], id: string, chain: Layer[]): boolean {
  for (const l of layers) {
    chain.push(l);
    if (l.id === id) return true;
    if (l.kind === "group" && findChain(l.children, id, chain)) return true;
    chain.pop();
  }
  return false;
}

function filterTree(layers: readonly Layer[], keep: (l: Layer) => boolean): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (!keep(l)) {
      // Even when we drop a group, walk its children so we keep their hidden
      // descendants — except those are guaranteed dropped too since their
      // parent is visible. So dropping the group is correct.
      continue;
    }
    if (l.kind === "group") {
      out.push({ ...l, children: filterTree(l.children, keep) });
    } else {
      out.push(l);
    }
  }
  return out;
}

function projectOntoCanvas(
  canvasW: number,
  canvasH: number,
  layer: RasterLayer,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(canvasW * canvasH * 4);
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const x0 = Math.max(0, layer.x);
  const y0 = Math.max(0, layer.y);
  const x1 = Math.min(canvasW, layer.x + lw);
  const y1 = Math.min(canvasH, layer.y + lh);
  for (let y = y0; y < y1; y++) {
    const srcRow = y - layer.y;
    for (let x = x0; x < x1; x++) {
      const srcCol = x - layer.x;
      const srcOff = (srcRow * lw + srcCol) * 4;
      const dstOff = (y * canvasW + x) * 4;
      out[dstOff] = layer.pixels.data[srcOff]!;
      out[dstOff + 1] = layer.pixels.data[srcOff + 1]!;
      out[dstOff + 2] = layer.pixels.data[srcOff + 2]!;
      out[dstOff + 3] = layer.pixels.data[srcOff + 3]!;
    }
  }
  return out;
}

function uniqueId(layers: readonly Layer[], base: string): string {
  const taken = new Set<string>();
  collectIds(layers, taken);
  if (!taken.has(base)) return base;
  let i = 1;
  while (taken.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

function collectIds(layers: readonly Layer[], out: Set<string>): void {
  for (const l of layers) {
    out.add(l.id);
    if (l.kind === "group") collectIds(l.children, out);
  }
}
